// Roles an admin can hand out from the dashboard.
export const assignableRoles = [
  'ADMIN',
  'READ_ONLY',
  'REVIEWER',
  'ANALYTICS_ONLY'
] as const

export type AssignableRole = (typeof assignableRoles)[number]

// A stored role the dashboard does not offer reads as UNKNOWN rather than borrowing another
// role's label, which would misreport what the user can actually do.
export type DashboardUserRole = AssignableRole | 'UNKNOWN'

export interface DashboardUser {
  id: string
  email: string
  role: DashboardUserRole
  assignedWarehouses: string[]
  // Access is revoked rather than deleted, so it can be restored and the user keeps their history.
  accessRevoked: boolean
}

export interface DashboardUserPermissions {
  role: AssignableRole
  assignedWarehouses: string[]
}

export function isAssignableRole(role: string): role is AssignableRole {
  return assignableRoles.some((assignable) => assignable === role)
}

// Only a reviewer is scoped to warehouses; every other role sees the whole group, so keeping a
// warehouse list on them would be a lie the next reviewer promotion silently inherits.
export function warehousesFor(
  role: AssignableRole,
  assignedWarehouses: string[]
): string[] {
  return role === 'REVIEWER' ? assignedWarehouses : []
}

export function hasValidPermissions(
  permissions: DashboardUserPermissions
): boolean {
  if (permissions.role !== 'REVIEWER') return true
  return permissions.assignedWarehouses.length > 0
}

const REVER_EMAIL_DOMAIN = '@itsrever.com'
// "*" is the every-group access only REVER staff get; REVER is the legacy staff group.
const STAFF_GROUPS = ['*', 'REVER']

// Staff by their REVER email, or by a staff group whatever the email. Missing data is not staff:
// a caller that must not act before it knows waits for the user to load.
export function isReverStaff(user: { email?: string; group?: string }) {
  const email = user.email?.trim().toLowerCase() ?? ''
  const group = user.group?.trim().toUpperCase() ?? ''
  return email.endsWith(REVER_EMAIL_DOMAIN) || STAFF_GROUPS.includes(group)
}
